import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./Table";
import { Event, EventStatus, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { Loader2, Search, CalendarPlus } from 'lucide-react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import EventTicketABI from '@/abis/EventTicket.json';
import { ethers } from 'ethers';
import BuyTicketButton from '../ui/BuyTicketButton';

const ActionButton: React.FC<{ onClick: () => void; className: string; children: React.ReactNode }> = ({ onClick, className, children }) => (
  <button
    onClick={onClick}
    className={`text-xs font-semibold px-2.5 py-1 rounded-md transition-colors ${className}`}>
    {children}
  </button>
);

const StatusPill: React.FC<{ status: EventStatus }> = ({ status }) => (
    <span
        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
            status === EventStatus.SCHEDULED
                ? 'bg-green-500/20 text-green-300'
                : status === EventStatus.CANCELLED
                ? 'bg-red-500/20 text-red-300'
                : 'bg-yellow-500/20 text-yellow-300' // PENDING
        }`}
    >
        {status}
    </span>
);

const TableActions: React.FC<{ 
  event: Event;
  role: UserRole; 
  onAction: (action: string, eventId: string) => void;
  onTicketPurchase: (eventId: string, userId: string, quantity: number) => void;
  currentUserId: string;
}> = ({ event, role, onAction, onTicketPurchase, currentUserId }) => {
  const handleAction = (action: string) => onAction(action, event.id);
  const isAdmin = role === UserRole.ADMIN || role === UserRole.SYSTEM_ADMIN_LIVE;
  
  return (
    <div className="space-x-2">
      <ActionButton onClick={() => handleAction('view')} className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/40">View</ActionButton>
      {isAdmin && (
          <ActionButton onClick={() => handleAction('delete')} className="bg-red-500/20 text-red-300 hover:bg-red-500/40">Delete</ActionButton>
      )}
      <BuyTicketButton 
        eventId={event.id} 
        currentUserId={currentUserId} 
        onTicketPurchase={onTicketPurchase} 
        quantity={1} 
      />
    </div>
  );
};

const EventRealDataTable = ({ currentUserRole, currentUserId }: { currentUserRole: UserRole, currentUserId: string }) => {
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFallback, setIsFallback] = useState(false);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, isConnected } = useWallet();
  const PAGE_SIZE = 10;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setIsFallback(false);
    addLog("Starting event data fetch.", LogLevel.INFO, LogCategory.DATA);

    if (provider && isConnected) {
      try {
        const ticketContract = new ethers.Contract(contractAddresses.EventTicket, EventTicketABI.abi, provider);
        
        const transferSingleFilter = ticketContract.filters.TransferSingle();
        const transferBatchFilter = ticketContract.filters.TransferBatch();

        const [singleEvents, batchEvents] = await Promise.all([
            ticketContract.queryFilter(transferSingleFilter),
            ticketContract.queryFilter(transferBatchFilter)
        ]);

        const eventIds = [...new Set([
            ...singleEvents.map((e: { args: { id: { toString: () => string; }; }; }) => e.args.id.toString()),
            ...batchEvents.flatMap((e: { args: { ids: { map: (arg0: (id: { toString: () => string; }) => string) => string[]; }; }; }) => e.args.ids.map((id: { toString: () => string; }) => id.toString()))
        ])];

        const eventPromises = eventIds.map(async (eventId) => {
            try {
                const uri = await ticketContract.uri(eventId);
                const metadataResponse = await fetch(uri.replace("ipfs://", "https://ipfs.io/ipfs/"));
                const metadata = await metadataResponse.json();

                const apiResponse = await fetch(`${API_BASE_URL}/api/events/${eventId}`, { credentials: 'include' });
                const apiData = apiResponse.ok ? await apiResponse.json() : {};

                return {
                    id: eventId,
                    name: metadata.name || `Event ${eventId}`,
                    startTime: apiData.startTime || new Date(metadata.startTime * 1000).toISOString(),
                    endTime: apiData.endTime || new Date(metadata.endTime * 1000).toISOString(),
                    status: apiData.status || EventStatus.PENDING,
                    venueName: apiData.venueName || 'N/A',
                    ...metadata,
                    ...apiData,
                };
            } catch (e) {
                addLog(`Failed to fetch metadata for event ${eventId}: ${(e as Error).message}`, LogLevel.WARN, LogCategory.DATA);
                const apiResponse = await fetch(`${API_BASE_URL}/api/events/${eventId}`, { credentials: 'include' });
                if (apiResponse.ok) return apiResponse.json();
                return null;
            }
        });

        const events = (await Promise.all(eventPromises)).filter(e => e) as Event[];
        setAllEvents(events);
        addLog(`Fetched ${events.length} on-chain events.`, LogLevel.SUCCESS, LogCategory.DATA);
        setIsLoading(false);
        return;
      } catch (e: unknown) {
        const onChainError = "Could not load on-chain event data. Falling back to API.";
        addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
        setError(onChainError);
        notify(onChainError, 'warn');
        setIsFallback(true);
      }
    }

    try {
      let apiData;
      const response = await fetch(`${API_BASE_URL}/api/events`, { credentials: 'include' });

      if (response.ok) {
        apiData = await response.json();
        addLog(`Fetched ${apiData.length} events from REAL API as fallback.`, LogLevel.INFO, LogCategory.API);
      } else {
        addLog(`Real API fallback failed, using mock data.`, LogLevel.WARN, LogCategory.API);
        const mockResponse = await fetch('/events.json');
        if (!mockResponse.ok) throw new Error('Real API and mock data both failed.');
        apiData = await mockResponse.json();
        notify('Displaying mock data. Wallet not connected and API failed.', 'warn');
      }
      
      if (Array.isArray(apiData)) {
        setAllEvents(apiData);
        setIsFallback(true);
      } else {
        throw new Error('Fetched off-chain data is not an array.');
      }

    } catch (e: unknown) {
      const fallbackError = `Critical: Failed to load any event data: ${(e as Error).message}`;
      addLog(fallbackError, LogLevel.CRITICAL, LogCategory.API);
      setError(fallbackError);
      notify(fallbackError, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (action: string, eventId: string) => {
    if (action === 'delete') {
      if (!window.confirm('Are you sure you want to delete this event?')) return;
      addLog(`Admin ${currentUserId} initiated deletion of event ${eventId}`, LogLevel.INFO, LogCategory.EVENT);
      try {
        const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, { method: 'DELETE', credentials: 'include' });
        if (response.status !== 204) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to delete event via API.');
        }
        setAllEvents(current => current.filter(e => e.id !== eventId));
        notify(`Event ${eventId} was successfully deleted.`, 'success');
        addLog(`Event ${eventId} successfully deleted by ${currentUserId}.`, LogLevel.INFO, LogCategory.EVENT);
      } catch (err) {
        notify((err as Error).message, 'error');
        addLog(`Error deleting event ${eventId}: ${(err as Error).message}`, LogLevel.ERROR, LogCategory.EVENT);
      }
    } else {
      notify(`Action '${action}' for event '${eventId}' is not yet implemented.`, 'info');
    }
  };

  const handleTicketPurchase = async (eventId: string, userId: string, quantity: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/event-tickets/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId, userId, quantity }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to purchase ticket.');
      }
      notify('Ticket purchased successfully!', 'success');
      fetchData(); // Refresh data
    } catch (err) {
      notify((err as Error).message, 'error');
    }
  };
  
  const filteredEvents = useMemo(() => {
    return allEvents.filter(e => 
      e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.venueName && e.venueName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [allEvents, searchTerm]);

  const displayedEvents = useMemo(() => {
      return filteredEvents.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredEvents, page]);

  const hasMore = page * PAGE_SIZE < filteredEvents.length;

  if (isLoading && allEvents.length === 0) return (
    <div className="flex justify-center items-center h-48">
      <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
      <p className="ml-4 text-kala-400">Loading event data...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Event Management</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
              <input
                  type="text"
                  placeholder="Search by name, status, or venue..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-64"
              />
          </div>
          <button className="bg-kala-secondary/90 hover:bg-kala-secondary text-kala-900 font-bold py-2 px-4 rounded-full flex items-center gap-2">
            <CalendarPlus className="w-4 h-4" />
            Create Event
          </button>
        </div>
      </div>
      {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
      {(!isConnected || isFallback) && (
        <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
          Note: Displaying fallback data. For real-time information, connect your wallet.
        </div>
      )}

      <Table>
        <TableCaption>A list of all scheduled events. {allEvents.length === 0 && !isLoading && "No events found."}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Event Name</TableHead>
            <TableHead>Venue</TableHead>
            <TableHead>Starts</TableHead>
            <TableHead>Ends</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayedEvents.map((event) => (
            <TableRow key={event.id}>
              <TableCell>{event.name}</TableCell>
              <TableCell>{event.venueName}</TableCell>
              <TableCell>{new Date(event.startTime).toLocaleString()}</TableCell>
              <TableCell>{new Date(event.endTime).toLocaleString()}</TableCell>
              <TableCell><StatusPill status={event.status} /></TableCell>
              <TableCell className="text-right">
                <TableActions 
                  event={event} 
                  role={currentUserRole} 
                  onAction={handleAction} 
                  onTicketPurchase={handleTicketPurchase} 
                  currentUserId={currentUserId} 
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {displayedEvents.length === 0 && !isLoading && (
        <div className="text-center p-8 text-kala-500">
            <p>No events found for your search term.</p>
        </div>
      )}

      <div className="flex justify-between items-center mt-6">
        <button 
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1 || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Previous
        </button>
        <span className="text-kala-400">Page {page} of {Math.ceil(filteredEvents.length / PAGE_SIZE)}</span>
        <button 
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore || isLoading}
          className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default EventRealDataTable;
